/**
 * Symbolic rules for the constraint classes the model builds, in the object
 * model (see ruleAst.ts): variables range over rows (`b: battery`), columns
 * are read with `.attr` (`b.efficiency`), applied references coerce to their
 * value (`P_discharge(n, t)`, `demand(n, t)`), and joins live in a quantifier's
 * `where` predicate (`sum(… for l in line if l.to == n)`).
 *
 * They are keyed by the same `group` name the model's constraints carry, so the
 * UI can show a rule above the rows it generated. Grounding them against the
 * data tables (the relations in schema.ts) is what builds the LP — there are no
 * hand-written builders left.
 *
 * These are the *defaults*. The app renders them to text at startup and edits
 * the text from then on (see ruleSet.ts), so a rule shown in the UI may no
 * longer be the one written here.
 *
 * NOTE: dcpf, soc_dynamics and initial_soc are faithful to the current model;
 * `balance` is a readable idealization — the per-load proportional split is
 * folded into the values of the `demand` relation, and the current isolated-bus
 * skip is omitted (a faithful grounding would filter buses with no lines).
 */

import {
  Rule,
  Objective,
  Expr,
  bind,
  ref,
  attr,
  apply,
  call,
  num,
  add,
  sub,
  mul,
  div,
  sum,
  eq,
  cmp,
  cond,
  range,
  define,
  str,
  neg,
  objective,
} from './ruleAst';
import { Catalog, catalogFor } from './schema';
import { typedCatalog } from './types';

/**
 * Generator output bounds:
 *   def gen_bounds(g: gen, t: timestep):
 *     g.min <= P_gen(g, t) <= gen_cap(g, t)
 *
 * The upper bound is the per-hour cap relation — fossil: the unit's max power;
 * wind/solar: its availability at that hour — computed in buildData.
 */
export const genBoundsRule: Rule = {
  name: 'gen_bounds',
  quantifiers: [bind('g', 'gen'), bind('t', 'timestep')],
  body: range(attr('g', 'min'), apply('P_gen', ref('g'), ref('t')), apply('gen_cap', ref('g'), ref('t'))),
};

/**
 * DC power flow on each line:
 *   def dcpf(l: line, t: timestep):
 *     P_line(l, t) == (theta(l.from, t) - theta(l.to, t)) / l.reactance
 *
 * `l.from` / `l.to` are the endpoint buses (used to index `theta`) and
 * `l.reactance` the scalar — all read straight off the line row.
 */
export const dcpfRule: Rule = {
  name: 'dcpf',
  quantifiers: [bind('l', 'line'), bind('t', 'timestep')],
  body: cmp(
    '=',
    apply('P_line', ref('l'), ref('t')),
    div(
      sub(
        apply('theta', attr('l', 'from'), ref('t')),
        apply('theta', attr('l', 'to'), ref('t')),
      ),
      attr('l', 'reactance'),
    ),
  ),
};

/**
 * Nodal power balance:
 *   def balance(n: bus, t: timestep):
 *     P_gen(n, t) + P_discharge(n, t) - P_charge(n, t)
 *       + sum(P_line(l, t) for l in line if l.from == n)
 *       - sum(P_line(l, t) for l in line if l.to == n)
 *       == demand(n, t)
 *
 * `demand(n, t)` is an applied data relation (single value column). The two sums
 * join on the current bus `n`. The line-flow sign follows the hand-written model:
 * a line counts `+flow` at its `from` bus and `-flow` at its `to` bus.
 */
export const balanceRule: Rule = {
  name: 'balance',
  quantifiers: [bind('n', 'bus', eq(attr('n', 'in_balance'), num(1))), bind('t', 'timestep')],
  body: cmp(
    '=',
    sub(
      add(
        sub(
          add(apply('P_gen', ref('n'), ref('t')), apply('P_discharge', ref('n'), ref('t'))),
          apply('P_charge', ref('n'), ref('t')),
        ),
        sum([bind('l', 'line', eq(attr('l', 'from'), ref('n')))], apply('P_line', ref('l'), ref('t'))),
      ),
      sum([bind('l', 'line', eq(attr('l', 'to'), ref('n')))], apply('P_line', ref('l'), ref('t'))),
    ),
    apply('demand', ref('n'), ref('t')),
  ),
};

/**
 * Initial state of charge:
 *   def initial_soc(b: battery):
 *     SOC(b, 0) == b.initial_soc
 */
export const initialSocRule: Rule = {
  name: 'initial_soc',
  quantifiers: [bind('b', 'battery')],
  body: cmp('=', apply('SOC', ref('b'), num(0)), attr('b', 'initial_soc')),
};

/**
 * Battery state-of-charge dynamics:
 *   def soc_dynamics(b: battery, t: timestep):
 *     SOC(b, t + 1) == SOC(b, t) + (sqrt(b.efficiency) * P_charge(b, t) - P_discharge(b, t) / sqrt(b.efficiency))
 *
 * The parenthesized group is the hour's *net* energy change — what went in less
 * what came out — so the rule reads "the next SOC is this one plus the net". The
 * parens are grouping only; `+ x - y` means the same thing.
 *
 * Round-trip efficiency is split across the two directions, sqrt(e) each way, so
 * that storing and retrieving a MWh loses `e` of it in total.
 *
 * Note that nothing here forbids charging and discharging in the same hour:
 * P_charge and P_discharge are independent continuous variables (see
 * charge_bounds / discharge_bounds), and there is no binary to make them
 * exclusive. What rules it out is that doing both is strictly wasteful — with
 * e < 1 the round trip destroys energy, and battery_cycling charges for the
 * throughput on top — so the optimum never pays for it.
 */
export const socDynamicsRule: Rule = {
  name: 'soc_dynamics',
  quantifiers: [bind('b', 'battery'), bind('t', 'timestep')],
  body: cmp(
    '=',
    apply('SOC', ref('b'), add(ref('t'), num(1))),
    add(
      apply('SOC', ref('b'), ref('t')),
      sub(
        mul(call('sqrt', attr('b', 'efficiency')), apply('P_charge', ref('b'), ref('t'))),
        div(apply('P_discharge', ref('b'), ref('t')), call('sqrt', attr('b', 'efficiency'))),
      ),
    ),
  ),
};

/**
 * Final state of charge (default): the battery ends at least 90% full.
 *   def final_soc(b: battery, h: horizon):
 *     SOC(b, h) >= 0.9 * b.initial_soc
 * `horizon` is the single last boundary N, so SOC(b, h) is the ending SOC.
 */
export const finalSocRule: Rule = {
  name: 'final_soc',
  quantifiers: [bind('b', 'battery'), bind('h', 'horizon')],
  body: cmp('>=', apply('SOC', ref('b'), ref('h')), mul(num(0.9), attr('b', 'initial_soc'))),
};

/**
 * Final state of charge when `requireMatchingSOC`: the battery ends where it began.
 *   def final_soc(b: battery, h: horizon):
 *     SOC(b, h) == b.initial_soc
 */
export const finalSocMatchRule: Rule = {
  name: 'final_soc',
  quantifiers: [bind('b', 'battery'), bind('h', 'horizon')],
  body: cmp('=', apply('SOC', ref('b'), ref('h')), attr('b', 'initial_soc')),
};

// ─── Derived data (definition rules: parameters = key, body = value) ───

/**
 * The per-hour cap on a generator, by fuel:
 *
 *   def gen_cap(g: gen, t: timestep):
 *     min(g.max, wind_strength(t)) if g.fuel == "wind"
 *     else min(g.max, solar_strength(t)) if g.fuel == "solar"
 *     else g.max
 *
 * A renewable unit is capped by whichever binds first, its nameplate maximum or
 * what the weather offers that hour; anything else only by its maximum. The
 * cases are a conditional rather than a rule apiece, so the relation has one
 * definition to read and one place to change.
 *
 * This used to be computed inside buildData. It's a rule now because it's a
 * *modelling* choice — that wind is capped rather than curtailable, that the
 * available power splits evenly across units — and those belong where they can
 * be read and changed.
 */
export const genCapRule: Rule = {
  name: 'gen_cap',
  quantifiers: [bind('g', 'gen'), bind('t', 'timestep')],
  body: define(
    cond(
      eq(attr('g', 'fuel'), str('wind')),
      call('min', attr('g', 'max'), apply('wind_strength', ref('t'))),
      cond(
        eq(attr('g', 'fuel'), str('solar')),
        call('min', attr('g', 'max'), apply('solar_strength', ref('t'))),
        attr('g', 'max'),
      ),
    ),
  ),
};

/** The rules that compute a data relation from other data. */
export const DERIVED_RULES: Rule[] = [genCapRule];

// ─── Variable bounds (ranged rules: quantifiers = index space, range = bounds) ───

/** `def charge_bounds(b: battery, t: timestep): 0 <= P_charge(b, t) <= b.max_charge` */
export const chargeBoundsRule: Rule = {
  name: 'charge_bounds',
  quantifiers: [bind('b', 'battery'), bind('t', 'timestep')],
  body: range(num(0), apply('P_charge', ref('b'), ref('t')), attr('b', 'max_charge')),
};

/** `def discharge_bounds(b: battery, t: timestep): 0 <= P_discharge(b, t) <= b.max_discharge` */
export const dischargeBoundsRule: Rule = {
  name: 'discharge_bounds',
  quantifiers: [bind('b', 'battery'), bind('t', 'timestep')],
  body: range(num(0), apply('P_discharge', ref('b'), ref('t')), attr('b', 'max_discharge')),
};

/** `def soc_bounds(b: battery, t: soc_time): b.min_energy <= SOC(b, t) <= b.max_energy` */
export const socBoundsRule: Rule = {
  name: 'soc_bounds',
  quantifiers: [bind('b', 'battery'), bind('t', 'soc_time')],
  body: range(attr('b', 'min_energy'), apply('SOC', ref('b'), ref('t')), attr('b', 'max_energy')),
};

/** `def flow_bounds(l: line, t: timestep): -l.rating <= P_line(l, t) <= l.rating` */
export const flowBoundsRule: Rule = {
  name: 'flow_bounds',
  quantifiers: [bind('l', 'line'), bind('t', 'timestep')],
  body: range(neg(attr('l', 'rating')), apply('P_line', ref('l'), ref('t')), attr('l', 'rating')),
};

/**
 * `def theta_bounds(n: bus if n.slack == 0, t: timestep): -100 <= theta(n, t) <= 100`
 * The `if` excludes the slack bus, so no `theta` variable exists for it — which
 * is what makes the slack angle drop out of the DC-flow equation at grounding.
 */
export const thetaBoundsRule: Rule = {
  name: 'theta_bounds',
  quantifiers: [bind('n', 'bus', eq(attr('n', 'slack'), num(0))), bind('t', 'timestep')],
  body: range(neg(num(100)), apply('theta', ref('n'), ref('t')), num(100)),
};

// ─── Objective ───────────────────────────────────────────────────────

/**
 * One named summand of the objective — a cost category. The objective is
 * `minimize cost` over the sum of its terms; the sense and the name are fixed, the
 * categories are the part worth editing.
 */
export interface CostTerm {
  name: string;
  expr: Expr;
}

/**
 * The default cost categories:
 *   generation       sum(g.cost * P_gen(g, t) for g in gen for t in timestep)
 *   battery_cycling  sum(0.01 * (P_charge(b, t) + P_discharge(b, t)) for b in battery for t in timestep)
 *
 * The second is a small per-MWh charge on moving energy through a battery. It
 * has no physical counterpart — it's there to stop the solver from cycling a
 * battery for free when doing so doesn't change the cost.
 */
export const COST_TERMS: CostTerm[] = [
  {
    name: 'generation',
    expr: sum([bind('g', 'gen'), bind('t', 'timestep')], mul(attr('g', 'cost'), apply('P_gen', ref('g'), ref('t')))),
  },
  {
    name: 'battery_cycling',
    expr: sum(
      [bind('b', 'battery'), bind('t', 'timestep')],
      mul(num(0.01), add(apply('P_charge', ref('b'), ref('t')), apply('P_discharge', ref('b'), ref('t')))),
    ),
  },
];

/** The whole objective: the cost categories, summed. */
export const costObjective: Objective = objective(
  'minimize',
  'cost',
  COST_TERMS.map((term) => term.expr).reduce(add),
);

/** The ranged rules that declare decision variables and their bounds. */
export const VARIABLE_RULES: Rule[] = [
  genBoundsRule,
  chargeBoundsRule,
  dischargeBoundsRule,
  socBoundsRule,
  flowBoundsRule,
  thetaBoundsRule,
];

/**
 * The comparison rules that become constraint rows. `requireMatchingSOC` selects
 * the final-SOC variant (end == start vs. end >= 90% of start).
 */
export function constraintRulesFor(requireMatchingSOC = false): Rule[] {
  return [
    dcpfRule,
    balanceRule,
    initialSocRule,
    socDynamicsRule,
    requireMatchingSOC ? finalSocMatchRule : finalSocRule,
  ];
}

/** The comparison rules with the default final-SOC (end >= 90%). */
export const CONSTRAINT_RULES: Rule[] = constraintRulesFor(false);

/**
 * All authored rules, in a stable order. This is the *default* problem: the UI
 * renders these to text once (see ruleSet.ts) and edits the text from then on,
 * so nothing should look a rule up here to display it — it would show the
 * built-in rule rather than the user's.
 */
export const ALL_RULES: Rule[] = [...DERIVED_RULES, ...VARIABLE_RULES, ...CONSTRAINT_RULES];

/**
 * The catalog of the default problem: the fixed data relations, plus the
 * decision relations the authored variable rules declare and the derived ones
 * the definition rules compute. Editing a rule re-derives its relation, so a
 * rule set carries its own catalog (see ruleSet.ts) — this is the one the
 * untouched defaults produce.
 */
export const GRID_CATALOG: Catalog = typedCatalog(
  catalogFor([...DERIVED_RULES, ...VARIABLE_RULES]),
  DERIVED_RULES,
);
